import { Field } from './field';
import { Commodity } from './commodity';
import { Crop } from './crop';
import {
  CommonChemicalElement,
  CommonPhenoStage,
  CommonPhenoStagePerCommodity,
} from './commonService';
import { Product } from './product';
import { ThemeConfig } from '../../../assets/data/enum/themeConfig';
import { SAASBRANDNAME } from '../../../assets/data/enum/saasBrandName.enum';

export class CropNutritionCommodity {
  id?: string = null;
  commodityId: number | number = null;
  cropId?: number = null;
  fieldId?: number = null;
  combinedEvaluations: CropNutritionElement[] = null;
  commodity?: Commodity = null;
  evaluations: CropNutritionEvaluationItem[] = [];
}

export class CropNutritionEvaluationItem {
  provider: string = null;
  id: string = null;
  cropId: number = null;
  // only used on front end side
  crop?: Crop = null;
  fieldId: number | number = null;
  commodityId: number = null;
  varietyId: number = null;
  status: string = null;
  field?: Field = null;
  commodity?: Commodity = null;
  currentEvaluationInstance: CropNutritionEvaluationInstance = null;
}

export class CropNutritionEvaluationInstance {
  id: string = null;
  provider: string = null;
  nutrients: CropNutritionElement[] = [];
  createdAt: string = null;
  lastUpdateDate: string = null;
  cropNutritionEvaluationId: string = null;
  cropNutritionEvaluationInstanceConfigurationItem: CropNutritionEvaluationInstanceConfigurationItem[] =
    [];
}

export class CropNutritionEvaluationInstanceConfigurationItem {
  cropNutritionConfigurationAttribute: string = null;
  defaultValue: boolean = false;
  externalId: any = null;
  id: any = null;
}

export class CropNutritionStageResponse {
  stages: CropNutritionStage[] = [];
  provider: string = null;
}

export class CropNutritionStage {
  phenoStageId: any = null;
  phenoStage?: CommonPhenoStage = null;
  // Pheno stage data matched from common service
  phenoStageLinkData?: CommonPhenoStagePerCommodity = null;
  deltaDayStart: number = null;
  deltaDayEnd: number = null;
  startDate: string = null;
  endDate: string = null;
  values: CropNutritionStageValue[] = [];
  children?: CropNutritionStage[] = [];
}

export class CropNutritionStageValue {
  elements: CropNutritionElement[] = [];
  product: CropNutritionStageProduct;
  amount: number = null;
  unitOfMeasure = null;
}

export class CropNutritionStageProduct extends Product {
  type: string = null;
  name: string = null;
  id: number = null;
  amount: number = null;
  co2eq: number = null;
  indirectGWPManufacturer: number = null;
  imgLogoUrl: string = null;
  localizedName: string = null;
}

export class CropNutritionBalanceResult {
  timestamp: string = null;
  amount: number = null;
}

export class CropNutritionBalance {
  elementId: string = null;
  cropYear: number = null;
  threshold: number = null;
  results: CropNutritionBalanceResult[] = null;
}

export class CropNutritionElement extends CommonChemicalElement {
  unit: string = null;
  availableUnits: string[] = [];
  type?: string = null;
  category?: string = null;
  normalizedValue?: number = null;
  translationalKey?: string = null;
  demandType?: string = null;
  unitOfMeasureId?: string = null;
}

export class RecapProduct {
  name: string = null;
  id: number = null;
  type: string = null;
  haDosage: number = null;
  totalDosage: number = null;
}

export class CropNutritionImproveRequest {
  soilTypeId?: any = null;
  preCropId?: any = null;
  postCropId?: any = null;
  intendedUse?: any = null;
  seedingFieldActivityId?: any = null;
  soilAnalysisId?: any = null;
}

export class CropNutritionNdviItem {
  cloudCoverage: number = null;
  snowCoverage: number = null;
  cropNutritionEvaluationInstanceId: string = null;
  id: string = null;
  imageUrl: string = null;
  referenceDate: string = null;
  valid: boolean = false;
}

export class CropNutritionAdditionalInfoConfigItem {
  id: string = null;
  translationKey: string = null;
  name: string = null;
  propertyName: string = null;
  required: boolean = false;
  ordinal: number = 0;
  valueType: string = null;
  defaultValue: number = 0;
  min: number = 0;
  max: number = 0;
  defaultOptionId: string = null;
  options: CropNutritionAdditionalInfoOptionConfig[] = [];
  current: CropNutritionAdditionalInfoOption = null;
}

export class CropNutritionAdditionalInfoOptionConfig {
  id: string = null;
  translationKey: string = null;
  value: any = null;
}

export class CropNutritionAdditionalInfoOption {
  providerSettingAdditionalParameterId: string = null;
  value?: number = null;
  optionId?: string = null;
}

export class YaraProductFamily {
  productRegionId: string = null;
  productFamilyId: string = null;
  mediaUri: YaraMediaUri[] = [];
}

export class YaraMediaUri {
  key: string = null;
  value: string = null;
}

export class ImproveCalculationEvent {
  field: Field;
  evaluationId: string = null;
  evaluationInstanceId: string = null;
  cropId: number;
}

export class NutritionDashboardPage {
  commodityId: string = null;
  page: number = null;
}

export class CropNutritionSuggestionValue {
  nutrientName: string = null;
  COMPUTED: CropNutritionElement = new CropNutritionElement();
  SUPPLIED: CropNutritionElement = new CropNutritionElement();
  REQUIRED: CropNutritionElement = new CropNutritionElement();
}

export const CropNutritionEvaluationStatus = {
  COMPLETE: 'COMPLETE',
  PENDING: 'PENDING',
  MISSING_DATA: 'MISSING_DATA',
  NOT_EVALUATED: 'NOT_EVALUATED',
};
export const CropNutritionElementCategories = {
  MACRONUTRIENT: 'MACRONUTRIENT',
  MICRONUTRIENT: 'MICRONUTRIENT',
};

export const CropNutritionElementTypes = {
  REQUIRED: 'REQUIRED',
  SUPPLIED: 'SUPPLIED',
  COMPUTED: 'COMPUTED',
  TO_SUPPLY: 'TO_SUPPLY',
};

export const NutritionProviders = {
  XFARM: ThemeConfig.brand != SAASBRANDNAME.BARILLA ? 'X_NPK' : 'AGROSAT',
  YARA_CNP: 'YARA_CNP',
};

export const NutritionConfigurationItems = {
  PRE_CROP: 'PRE_CROP',
  POST_CROP: 'POST_CROP',
  SOIL_ANALYSIS: 'SOIL_ANALYSIS',
  INTENDED_USE: 'INTENDED_USE',
  SEEDING_FIELD_ACTIVITY: 'SEEDING_FIELD_ACTIVITY',
};

export const DemandType = {
  CROP_DEMAND: 'CROP_DEMAND',
  SOIL_DEMAND: 'SOIL_DEMAND',
};

export const AdditionalInfoConfigTypes = {
  LIST: 'LIST',
  NUMBER: 'NUMBER',
  TEXT: 'TEXT',
  BOOLEAN: 'BOOLEAN',
};

export const YARA_PRODUCT_FAMILY_MAPPING: YaraProductFamily[] = [
  {
    productRegionId: '8b1858f4-2c35-4f6c-ba41-e6ee4e0276c1',
    productFamilyId: '177af68d-6166-42de-8e36-6ab305b346c0',
    mediaUri: [
      {
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/YaraFamily.PNG',
        key: 'YaraFamily',
      },
      {
        key: 'DEFAULT',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/YaraFamily.PNG',
      },
      {
        key: 'WhiteLogo',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/YaraFamilyWhiteLogo.svg',
      },
    ],
  },
  {
    productRegionId: '3ff842f8-3128-4abe-bc32-0d242bfd6644',
    productFamilyId: '177af68d-6166-42de-8e36-6ab305b346c0',
    mediaUri: [
      {
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/YaraFamily.PNG',
        key: 'YaraFamily',
      },
      {
        key: 'DEFAULT',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/YaraFamily.PNG',
      },
      {
        key: 'WhiteLogo',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/YaraFamilyWhiteLogo.svg',
      },
    ],
  },
  {
    productRegionId: '3d958066-1f85-46d7-af04-1c26e8cdfc9b',
    productFamilyId: '4963ac76-183b-484e-9b4c-3ce2607c3315',
    mediaUri: [
      {
        key: 'DEFAULT',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/YaraRega.svg',
      },
      {
        key: 'WhiteLogo',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/YaraRegaWhiteLogo.svg',
      },
    ],
  },
  {
    productRegionId: '07c97be4-58f0-4da8-b651-8909041249dc',
    productFamilyId: 'cc36047f-60cd-4935-b48a-8b9255e36e60',
    mediaUri: [
      {
        key: 'DEFAULT',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/YaraTera.svg',
      },
      {
        key: 'WhiteLogo',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/YaraTeraWhiteLogo.svg',
      },
    ],
  },
  {
    productRegionId: '779d09a6-6772-49cd-805b-fe8e57bdea23',
    productFamilyId: 'ca7dc04a-0803-4ac0-a583-c13cb7fa44cb',
    mediaUri: [
      {
        key: 'DEFAULT',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/YaraMila.svg',
      },
      {
        key: 'WhiteLogo',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/YaraMilaWhiteLogo.svg',
      },
    ],
  },
  {
    productRegionId: '9c4d31b8-97c8-4024-b486-35e77c645d67',
    productFamilyId: 'b849b1fa-36d1-4e08-925f-2948b18c8d1b',
    mediaUri: [
      {
        key: 'DEFAULT',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/YaraVera.svg',
      },
      {
        key: 'WhiteLogo',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/YaraVeraWhiteLogo.svg',
      },
    ],
  },
  {
    productRegionId: 'bade9281-883a-44a4-83d5-319226856459',
    productFamilyId: 'd5b8c573-3dd4-437a-b334-7be1e48b92eb',
    mediaUri: [
      {
        key: 'DEFAULT',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/YaraBela.svg',
      },
      {
        key: 'WhiteLogo',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/YaraBelaWhiteLogo.svg',
      },
    ],
  },
  {
    productRegionId: '062d67cf-a826-4b92-bdd5-9807795c255a',
    productFamilyId: 'ca7dc04a-0803-4ac0-a583-c13cb7fa44cb',
    mediaUri: [
      {
        key: 'DEFAULT',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/YaraMila.svg',
      },
      {
        key: 'WhiteLogo',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/YaraMilaWhiteLogo.svg',
      },
    ],
  },
  {
    productRegionId: '7800f6f7-2334-4902-b525-57b974527c4b',
    productFamilyId: 'ca7dc04a-0803-4ac0-a583-c13cb7fa44cb',
    mediaUri: [
      {
        key: 'DEFAULT',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/YaraMila.svg',
      },
      {
        key: 'WhiteLogo',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/YaraMilaWhiteLogo.svg',
      },
    ],
  },
  {
    productRegionId: 'd76c2c40-ee95-43a8-807e-2a6d38386e3f',
    productFamilyId: 'd5b8c573-3dd4-437a-b334-7be1e48b92eb',
    mediaUri: [
      {
        key: 'DEFAULT',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/YaraBela.svg',
      },
      {
        key: 'WhiteLogo',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/YaraBelaWhiteLogo.svg',
      },
    ],
  },
  {
    productRegionId: '79eec933-68f0-4d07-a341-98dc422d7494',
    productFamilyId: 'd5b8c573-3dd4-437a-b334-7be1e48b92eb',
    mediaUri: [
      {
        key: 'DEFAULT',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/YaraBela.svg',
      },
      {
        key: 'WhiteLogo',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/YaraBelaWhiteLogo.svg',
      },
    ],
  },
  {
    productRegionId: 'b9c5c998-bb82-4f76-8747-8e349d6f6908',
    productFamilyId: '6d0ce6d2-3f99-415a-8572-d7f57a4d200d',
    mediaUri: [
      {
        key: 'DEFAULT',
        value: 'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/Organic.svg',
      },
      {
        key: 'ATFARM',
        value: 'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/Cow.svg',
      },
      {
        key: 'WhiteLogo',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/OrganicWhiteLogo.svg',
      },
    ],
  },
  {
    productRegionId: 'b5f73652-5414-49be-b955-44311883c725',
    productFamilyId: '6d0ce6d2-3f99-415a-8572-d7f57a4d200d',
    mediaUri: [
      {
        key: 'DEFAULT',
        value: 'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/Organic.svg',
      },
      {
        key: 'ATFARM',
        value: 'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/Cow.svg',
      },
      {
        key: 'WhiteLogo',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/OrganicWhiteLogo.svg',
      },
    ],
  },
  {
    productRegionId: '75a2f9d7-cb39-4c7a-9775-aa33cd8276d1',
    productFamilyId: '6d0ce6d2-3f99-415a-8572-d7f57a4d200d',
    mediaUri: [
      {
        key: 'DEFAULT',
        value: 'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/Organic.svg',
      },
      {
        key: 'ATFARM',
        value: 'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/Cow.svg',
      },
      {
        key: 'WhiteLogo',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/OrganicWhiteLogo.svg',
      },
    ],
  },
  {
    productRegionId: 'b058e818-7ccf-4f8e-95fe-caa41ecb3083',
    productFamilyId: 'e2833d21-2cc1-4ad3-b9a8-05fe78f7725d',
    mediaUri: [
      {
        key: 'DEFAULT',
        value: 'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/Organic.svg',
      },
      {
        key: 'ATFARM',
        value: 'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/Chicken.svg',
      },
      {
        key: 'WhiteLogo',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/OrganicWhiteLogo.svg',
      },
    ],
  },
  {
    productRegionId: '4f155d8d-191f-4e77-920b-c1c95e3b90a0',
    productFamilyId: 'e2833d21-2cc1-4ad3-b9a8-05fe78f7725d',
    mediaUri: [
      {
        key: 'DEFAULT',
        value: 'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/Organic.svg',
      },
      {
        key: 'ATFARM',
        value: 'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/Chicken.svg',
      },
      {
        key: 'WhiteLogo',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/OrganicWhiteLogo.svg',
      },
    ],
  },
  {
    productRegionId: '9666000d-e498-45a0-ac22-5ff7197555df',
    productFamilyId: 'ca7dc04a-0803-4ac0-a583-c13cb7fa44cb',
    mediaUri: [
      {
        key: 'DEFAULT',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/YaraMila.svg',
      },
      {
        key: 'WhiteLogo',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/YaraMilaWhiteLogo.svg',
      },
    ],
  },
  {
    productRegionId: 'a0e45780-b3d4-4450-b1c3-b21fcabf4f7c',
    productFamilyId: '177af68d-6166-42de-8e36-6ab305b346c0',
    mediaUri: [
      {
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/YaraFamily.PNG',
        key: 'YaraFamily',
      },
      {
        key: 'DEFAULT',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/YaraFamily.PNG',
      },
      {
        key: 'WhiteLogo',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/YaraFamilyWhiteLogo.svg',
      },
    ],
  },
  {
    productRegionId: 'f01201b4-9862-423a-986b-a253eb1c0d6f',
    productFamilyId: '177af68d-6166-42de-8e36-6ab305b346c0',
    mediaUri: [
      {
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/YaraFamily.PNG',
        key: 'YaraFamily',
      },
      {
        key: 'DEFAULT',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/YaraFamily.PNG',
      },
      {
        key: 'WhiteLogo',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/YaraFamilyWhiteLogo.svg',
      },
    ],
  },
  {
    productRegionId: 'd4ea5f92-e34b-4584-be80-224edf4d1e99',
    productFamilyId: '39cc281f-2da0-4b51-8657-7cf23475d762',
    mediaUri: [
      {
        key: 'DEFAULT',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/YaraVita.svg',
      },
      {
        key: 'WhiteLogo',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/YaraVitaWhiteLogo.svg',
      },
    ],
  },
  {
    productRegionId: 'ed91b494-f487-453c-b3a6-14b917904cba',
    productFamilyId: '39cc281f-2da0-4b51-8657-7cf23475d762',
    mediaUri: [
      {
        key: 'DEFAULT',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/YaraVita.svg',
      },
      {
        key: 'WhiteLogo',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/YaraVitaWhiteLogo.svg',
      },
    ],
  },
  {
    productRegionId: 'e0045535-9a30-4c6b-888a-e2088bd0efd1',
    productFamilyId: '39cc281f-2da0-4b51-8657-7cf23475d762',
    mediaUri: [
      {
        key: 'DEFAULT',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/YaraVita.svg',
      },
      {
        key: 'WhiteLogo',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/YaraVitaWhiteLogo.svg',
      },
    ],
  },
  {
    productRegionId: 'b1422104-3fd1-4d89-b13b-aeeb05f9face',
    productFamilyId: '39cc281f-2da0-4b51-8657-7cf23475d762',
    mediaUri: [
      {
        key: 'DEFAULT',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/YaraVita.svg',
      },
      {
        key: 'WhiteLogo',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/YaraVitaWhiteLogo.svg',
      },
    ],
  },
  {
    productRegionId: 'eddedddc-cc70-46e1-8ce1-8bbecae5980f',
    productFamilyId: '39cc281f-2da0-4b51-8657-7cf23475d762',
    mediaUri: [
      {
        key: 'DEFAULT',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/YaraVita.svg',
      },
      {
        key: 'WhiteLogo',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/YaraVitaWhiteLogo.svg',
      },
    ],
  },
  {
    productRegionId: '912d85c4-b387-468b-ac92-9d7ae23e57cf',
    productFamilyId: '39cc281f-2da0-4b51-8657-7cf23475d762',
    mediaUri: [
      {
        key: 'DEFAULT',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/YaraVita.svg',
      },
      {
        key: 'WhiteLogo',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/YaraVitaWhiteLogo.svg',
      },
    ],
  },
  {
    productRegionId: 'feb1f5eb-dd09-44c4-84d0-f006692a50c6',
    productFamilyId: 'ca7dc04a-0803-4ac0-a583-c13cb7fa44cb',
    mediaUri: [
      {
        key: 'DEFAULT',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/YaraMila.svg',
      },
      {
        key: 'WhiteLogo',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/YaraMilaWhiteLogo.svg',
      },
    ],
  },
  {
    productRegionId: 'f34b7a49-f8b8-4e8a-ab21-79353fbaadb8',
    productFamilyId: 'b849b1fa-36d1-4e08-925f-2948b18c8d1b',
    mediaUri: [
      {
        key: 'DEFAULT',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/YaraVera.svg',
      },
      {
        key: 'WhiteLogo',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/YaraVeraWhiteLogo.svg',
      },
    ],
  },
  {
    productRegionId: '619b60dd-9a9e-49bc-b8ed-69110bd0da61',
    productFamilyId: 'b849b1fa-36d1-4e08-925f-2948b18c8d1b',
    mediaUri: [
      {
        key: 'DEFAULT',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/YaraVera.svg',
      },
      {
        key: 'WhiteLogo',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/YaraVeraWhiteLogo.svg',
      },
    ],
  },
  {
    productRegionId: '81f1091a-7b69-4ee4-946f-cfca22450974',
    productFamilyId: 'b849b1fa-36d1-4e08-925f-2948b18c8d1b',
    mediaUri: [
      {
        key: 'DEFAULT',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/YaraVera.svg',
      },
      {
        key: 'WhiteLogo',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/YaraVeraWhiteLogo.svg',
      },
    ],
  },
  {
    productRegionId: '3e36e14e-9647-495a-955e-336c80602457',
    productFamilyId: 'b849b1fa-36d1-4e08-925f-2948b18c8d1b',
    mediaUri: [
      {
        key: 'DEFAULT',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/YaraVera.svg',
      },
      {
        key: 'WhiteLogo',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/YaraVeraWhiteLogo.svg',
      },
    ],
  },
  {
    productRegionId: '5239f1e0-b2d3-45d6-a367-8c95af086ce6',
    productFamilyId: 'ca7dc04a-0803-4ac0-a583-c13cb7fa44cb',
    mediaUri: [
      {
        key: 'DEFAULT',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/YaraMila.svg',
      },
      {
        key: 'WhiteLogo',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/YaraMilaWhiteLogo.svg',
      },
    ],
  },
  {
    productRegionId: 'ee6bc753-f24d-47f2-afbf-9051ffa0f01d',
    productFamilyId: '079a5655-093d-4a93-9c2e-1813341878ec',
    mediaUri: [
      {
        key: 'DEFAULT',
        value: 'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/Generic.svg',
      },
      {
        key: 'WhiteLogo',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/GenericWhiteLogo.svg',
      },
    ],
  },
  {
    productRegionId: '165de227-4261-45f6-ad12-65719de38b7a',
    productFamilyId: '079a5655-093d-4a93-9c2e-1813341878ec',
    mediaUri: [
      {
        key: 'DEFAULT',
        value: 'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/Generic.svg',
      },
      {
        key: 'WhiteLogo',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/GenericWhiteLogo.svg',
      },
    ],
  },
  {
    productRegionId: 'a9296cc5-9c4a-4968-9979-b6d0992a89be',
    productFamilyId: '079a5655-093d-4a93-9c2e-1813341878ec',
    mediaUri: [
      {
        key: 'DEFAULT',
        value: 'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/Generic.svg',
      },
      {
        key: 'WhiteLogo',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/GenericWhiteLogo.svg',
      },
    ],
  },
  {
    productRegionId: 'c530432b-672c-4875-94fe-c39e3f86bf80',
    productFamilyId: '079a5655-093d-4a93-9c2e-1813341878ec',
    mediaUri: [
      {
        key: 'DEFAULT',
        value: 'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/Generic.svg',
      },
      {
        key: 'WhiteLogo',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/GenericWhiteLogo.svg',
      },
    ],
  },
  {
    productRegionId: 'b3d7787f-dd1b-413f-8d75-d12348e43e72',
    productFamilyId: '95444e88-25e9-40fc-801b-147cdf319de0',
    mediaUri: [
      {
        key: 'DEFAULT',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/YaraLiva.svg',
      },
      {
        key: 'WhiteLogo',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/YaraLivaWhiteLogo.svg',
      },
    ],
  },
  {
    productRegionId: '15d380d3-509c-486d-ac29-cc0fb1700c9e',
    productFamilyId: 'd5b8c573-3dd4-437a-b334-7be1e48b92eb',
    mediaUri: [
      {
        key: 'DEFAULT',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/YaraBela.svg',
      },
      {
        key: 'WhiteLogo',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/YaraBelaWhiteLogo.svg',
      },
    ],
  },
  {
    productRegionId: '1aa7b749-2e8b-4f80-a6dd-1fc50cbb090e',
    productFamilyId: 'd5b8c573-3dd4-437a-b334-7be1e48b92eb',
    mediaUri: [
      {
        key: 'DEFAULT',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/YaraBela.svg',
      },
      {
        key: 'WhiteLogo',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/YaraBelaWhiteLogo.svg',
      },
    ],
  },
  {
    productRegionId: '6163b092-5bf6-462e-bde5-a6736a449297',
    productFamilyId: 'd5b8c573-3dd4-437a-b334-7be1e48b92eb',
    mediaUri: [
      {
        key: 'DEFAULT',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/YaraBela.svg',
      },
      {
        key: 'WhiteLogo',
        value:
          'https://polaris-axial-static-medias.yarapolaris.com/product/brand_logo/YaraBelaWhiteLogo.svg',
      },
    ],
  },
];

export interface NutritionYaraMarketingConsents {
  marketingConsent: boolean;
}

export interface NutritionYaraUser extends NutritionYaraMarketingConsents {
  modified: string;
  userOfFarm: boolean;
  dataConsent: boolean;
}
